import api from '../../utils/api';

export type DoctorBlogApproval = 'pending' | 'approved' | 'rejected';

export type DoctorBlogAuthor = {
  type?: string | null;
  name?: string | null;
  image?: string | null;
  qualificationDisplay?: string | null;
};

export type DoctorBlog = {
  blogId: string;
  title: string;
  content: string;
  featuredImage?: string | null;
  hasThumbnail?: boolean;
  tags?: string[] | null;
  referenceLinks?: string[] | null;
  status: 'draft' | 'published';
  adminApproval?: DoctorBlogApproval | null;
  rejectionReason?: string | null;
  blogDate?: string | null;
  authorId: string;
  authorType: 'doctor' | 'blog_writer' | 'writer' | 'admin';
  categoryId: string;
  category?: {
    categoryId?: string | null;
    name?: string | null;
    createdBy?: 'doctor' | 'admin' | null;
    vendorId?: string | null;
    approvalStatus?: 'pending' | 'approved' | 'rejected' | null;
    approvalReason?: string | null;
  } | null;
  author?: DoctorBlogAuthor | null;
  doctor?: {
    doctorName?: string | null;
    educationalQualifications?: string[] | null;
  } | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  slug?: string | null;
  focusKeyword?: string | null;
  secondaryKeywords?: string | null;
  recommendedSchema?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type DoctorBlogSeoInput = {
  metaTitle?: string | null;
  metaDescription?: string | null;
  slug?: string | null;
  focusKeyword?: string | null;
  secondaryKeywords?: string | null;
  recommendedSchema?: string | null;
};

export type ListDoctorBlogsParams = {
  page?: number;
  limit?: number;
  adminApproval?: DoctorBlogApproval | '';
  status?: 'draft' | 'published' | '';
  search?: string;
  /** Doctor vendorId when authorType is doctor */
  authorId?: string;
};

export type ListDoctorBlogsResult = {
  blogs: DoctorBlog[];
  totalBlogs: number;
  currentPage: number;
  totalPages: number;
  limit: number;
};

/** Let the browser set multipart boundary — a bare Content-Type breaks file uploads. */
const stripMultipartContentType = (data: FormData, headers: any) => {
  if (headers) {
    if (typeof headers.delete === 'function') {
      headers.delete('Content-Type');
    } else {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
  }
  return data;
};

export const listDoctorBlogs = async (
  params: ListDoctorBlogsParams = {}
): Promise<ListDoctorBlogsResult> => {
  const response = await api.get('/master-admin/blogs', {
    params: {
      authorType: 'doctor',
      page: params.page ?? 1,
      limit: params.limit ?? 50,
      ...(params.adminApproval ? { adminApproval: params.adminApproval } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.search?.trim() ? { search: params.search.trim() } : {}),
      ...(params.authorId?.trim() ? { authorId: params.authorId.trim() } : {}),
    },
  });
  const data = response.data || {};
  return {
    blogs: Array.isArray(data.blogs) ? data.blogs : [],
    totalBlogs: Number(data.totalBlogs ?? 0),
    currentPage: Number(data.currentPage ?? 1),
    totalPages: Number(data.totalPages ?? 1),
    limit: Number(data.limit ?? 50),
  };
};

export const getDoctorBlogById = async (blogId: string): Promise<DoctorBlog> => {
  const response = await api.get(`/master-admin/blogs/${blogId}`);
  return response.data?.blog;
};

export const updateDoctorBlogThumbnail = async (blogId: string, file: File) => {
  const fd = new FormData();
  fd.append('featuredImage', file);
  return api.patch(`/master-admin/blogs/${blogId}/thumbnail`, fd, {
    transformRequest: [(data, headers) => stripMultipartContentType(data as FormData, headers)],
  });
};

export const approveDoctorBlog = async (
  blogId: string,
  options: {
    thumbnailFile?: File | null;
    seo?: DoctorBlogSeoInput | null;
    approveCategory?: boolean;
  } = {}
) => {
  const fd = new FormData();
  if (options.thumbnailFile) {
    fd.append('featuredImage', options.thumbnailFile);
  }
  if (options.approveCategory) {
    fd.append('approveCategory', 'true');
  }
  const seo = options.seo;
  if (seo) {
    if (seo.metaTitle != null) fd.append('metaTitle', seo.metaTitle);
    if (seo.metaDescription != null) fd.append('metaDescription', seo.metaDescription);
    if (seo.slug != null) fd.append('slug', seo.slug);
    if (seo.focusKeyword != null) fd.append('focusKeyword', seo.focusKeyword);
    if (seo.secondaryKeywords != null) fd.append('secondaryKeywords', seo.secondaryKeywords);
    if (seo.recommendedSchema != null) fd.append('recommendedSchema', seo.recommendedSchema);
  }
  return api.post(`/master-admin/blogs/${blogId}/approve`, fd, {
    transformRequest: [(data, headers) => stripMultipartContentType(data as FormData, headers)],
  });
};

export const rejectDoctorBlog = async (blogId: string, rejectionReason?: string) => {
  return api.post(`/master-admin/blogs/${blogId}/reject`, {
    rejectionReason: rejectionReason?.trim() || undefined,
  });
};
